"use client";

import { DeparturePriceBucket } from "@b2bportal/air-shopping-api";
import { UseDatepickerProps } from "@datepicker-react/hooks";
import { useI18nContext } from "@hopper-b2b/i18n";
import { Deal } from "@hopper-proto/hopper/dealfeed/proto/v1/Deal";
import {
  Box,
  Button,
  ClickAwayListener,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  Grow,
  IconButton,
  Popper,
  PopperProps,
  Stack,
  TextFieldProps,
  Theme,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import dayjs from "dayjs";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Close } from "../../assets";
import { CalendarLegend } from "../CalendarLegend";
import { DateCalendar } from "../DateCalendar";
import { PickerType } from "../DateCalendar/DateCalendarContext";
import { FilterBar, FilterItem } from "../DateCalendar/FilterBar";
import { SearchField } from "../SearchField";

const DEFAULT_DATE_FORMAT = "MMM D";

export type CalendarProps = {
  onClose: () => void;
  onChooseDates?: () => void;
} & Pick<PopperProps, "anchorEl" | "open"> &
  Pick<
    UseDatepickerProps,
    | "startDate"
    | "endDate"
    | "focusedInput"
    | "onDatesChange"
    | "minBookingDays"
    | "minBookingDate"
    | "maxBookingDate"
  > & {
    pickerType?: PickerType;
    departureDateBuckets?: DeparturePriceBucket[];
    filters?: FilterItem[];
    onDepartureSelect?: (date: Date) => void;
    initialMonth?: Date;
    forceModal?: boolean;
    deals?: Deal[];
    forceDisplayBucketsAndDeals?: boolean;
    text?: {
      numberOfDays?: (count: number) => void;
    };
  };

export const Calendar = ({
  open,
  onClose,
  onChooseDates,
  anchorEl,
  startDate,
  endDate,
  focusedInput,
  onDatesChange,
  pickerType = PickerType.RANGE,
  departureDateBuckets = [],
  minBookingDays,
  minBookingDate,
  maxBookingDate,
  filters = [],
  initialMonth,
  forceModal,
  deals = [],
  forceDisplayBucketsAndDeals = false,
  text,
}: CalendarProps) => {
  const { t } = useI18nContext();
  const { tokens } = useTheme();
  const isTablet = !useMediaQuery(({ breakpoints }: Theme) =>
    breakpoints.up("desktop")
  );
  const areDatesSelected = !!(pickerType === PickerType.RANGE
    ? startDate && endDate
    : startDate);

  const {
    numberOfDays = (count: number) =>
      t("searchBar.nights", {
        count,
      }),
  } = text ?? {};

  const datesLabel = useMemo(() => {
    switch (pickerType) {
      case PickerType.DAY:
        return t("searchBar.datesLabel_one");
      case PickerType.RANGE:
        return t("searchBar.datesLabel");
      default:
        return t("searchBar.datesLabel");
    }
  }, [pickerType, t]);

  return (
    <>
      <Popper
        open={!forceModal && open && !isTablet}
        anchorEl={anchorEl}
        transition
        disablePortal
        sx={{
          zIndex: 1201,
        }}
      >
        {({ TransitionProps }) => (
          <Grow
            {...TransitionProps}
            style={{
              transformOrigin: "left top",
            }}
          >
            <Box
              margin={150}
              bgcolor={({ tokens }) => tokens.surface.main}
              borderRadius={`${tokens.spacing[250]}px`}
              boxShadow={`0px 4px 14px 0px rgba(0, 0, 0, 0.25)`}
              overflow="hidden"
            >
              <ClickAwayListener onClickAway={onClose}>
                <Box padding={500} width="780px">
                  {isTablet &&
                    departureDateBuckets &&
                    departureDateBuckets.length > 0 && (
                      <CalendarLegend
                        paddingY={200}
                        keys={departureDateBuckets.map(
                          (bucket) => bucket.legend
                        )}
                      />
                    )}
                  <DateCalendar
                    startDate={startDate}
                    endDate={endDate}
                    focusedInput={focusedInput}
                    onDatesChange={onDatesChange}
                    pickerType={pickerType}
                    departureDateBuckets={departureDateBuckets}
                    minBookingDays={minBookingDays}
                    minBookingDate={minBookingDate}
                    maxBookingDate={maxBookingDate}
                    initialMonth={initialMonth}
                    deals={deals}
                    forceDisplayBucketsAndDeals={forceDisplayBucketsAndDeals}
                  />
                  {!isTablet &&
                    departureDateBuckets &&
                    departureDateBuckets.length > 0 && (
                      <Stack
                        gap={300}
                        paddingTop={300}
                        display={{
                          mobile: "none",
                          desktop: "flex",
                        }}
                      >
                        <Divider flexItem />
                        <CalendarLegend
                          keys={departureDateBuckets.map(
                            (bucket) => bucket.legend
                          )}
                        />
                      </Stack>
                    )}
                </Box>
              </ClickAwayListener>
            </Box>
          </Grow>
        )}
      </Popper>
      <Dialog
        open={open && (!!forceModal || isTablet)}
        fullScreen={isTablet}
        maxWidth="xl"
        PaperProps={{
          sx: isTablet
            ? {}
            : {
                borderRadius: ({ tokens }) => tokens.cornerRadius.radiusXl,
              },
        }}
        onClose={onClose}
      >
        <DialogTitle
          borderBottom={{
            desktop: `1px solid ${tokens.surface.borderDivider}`,
          }}
          sx={{
            padding: 150,
          }}
        >
          <Typography variant="titleLarge" align="center">
            {datesLabel}
          </Typography>
          <IconButton
            onClick={onClose}
            sx={({ tokens }) => ({
              position: "absolute",
              top: `${tokens.spacing[150]}px`,
              right: `${tokens.spacing[150]}px`,
              padding: `${tokens.spacing[50]}px`,
              color: tokens.text.primary,
            })}
          >
            <Close width="16px" height="16px" />
          </IconButton>
        </DialogTitle>
        <DialogContent
          sx={{
            p: 0,
          }}
        >
          <Stack
            position="relative"
            width="100%"
            height="100%"
            sx={{
              p: { mobile: 200, desktop: 400 },
              paddingTop: { mobile: 0, desktop: 0 },
            }}
          >
            <Box sx={{ paddingBottom: { desktop: 400, mobile: 0 } }}>
              <FilterBar filters={filters} />
            </Box>
            {isTablet &&
              departureDateBuckets &&
              departureDateBuckets.length > 0 && (
                <CalendarLegend
                  paddingY={200}
                  keys={departureDateBuckets.map((bucket) => bucket.legend)}
                />
              )}
            <DateCalendar
              startDate={startDate}
              endDate={endDate}
              focusedInput={focusedInput}
              onDatesChange={onDatesChange}
              pickerType={pickerType}
              departureDateBuckets={departureDateBuckets}
              minBookingDays={minBookingDays}
              minBookingDate={minBookingDate}
              maxBookingDate={maxBookingDate}
              initialMonth={initialMonth}
              deals={deals}
              forceDisplayBucketsAndDeals={forceDisplayBucketsAndDeals}
            />
            {!isTablet && (
              <Stack
                gap={300}
                paddingTop={300}
                display={{
                  mobile: "none",
                  desktop: "flex",
                }}
                sx={{
                  position: "relative",
                  minHeight: "44px",
                }}
              >
                {departureDateBuckets && departureDateBuckets.length > 0 && (
                  <>
                    <Divider flexItem />
                    <CalendarLegend
                      keys={departureDateBuckets.map((bucket) => bucket.legend)}
                      height={({ tokens }) =>
                        `${tokens.typography.fontSize[12]}px`
                      }
                    />
                  </>
                )}
                <Button
                  onClick={() => {
                    onClose();
                    onChooseDates?.();
                  }}
                  variant="filled"
                  size="medium"
                  sx={{
                    visibility: areDatesSelected ? "visible" : "hidden",
                    position: "absolute",
                    bottom: 0,
                    right: 0,
                  }}
                >
                  <Typography variant="buttonLarge">
                    {t(
                      pickerType === PickerType.RANGE
                        ? "searchBar.chooseDates"
                        : "searchBar.chooseDate"
                    )}
                  </Typography>
                </Button>
              </Stack>
            )}
          </Stack>
          {isTablet && areDatesSelected && (
            <Stack
              width="100%"
              py={150}
              px={({ tokens }) => tokens.container.padding}
              gap={125}
              position="fixed"
              bottom={0}
              sx={({ tokens }) => ({
                borderTop: `1px solid ${tokens.surface.borderDivider}`,
                bgcolor: tokens.surface.main,
              })}
              zIndex={1}
            >
              <Typography variant="labelMedium" textAlign="center">
                {pickerType === PickerType.RANGE
                  ? `${dayjs(startDate).format("MMM D")} - ${dayjs(
                      endDate
                    ).format(
                      startDate?.getMonth() === endDate?.getMonth()
                        ? "D"
                        : "MMM D"
                    )} (${numberOfDays(dayjs(endDate).diff(startDate, "day"))})`
                  : dayjs(startDate).format("MMM D")}
              </Typography>
              <Button
                onClick={() => {
                  onClose();
                  onChooseDates?.();
                }}
                variant="filled"
                size="large"
              >
                <Typography variant="buttonLarge">
                  {t(
                    pickerType === PickerType.RANGE
                      ? "searchBar.chooseDates"
                      : "searchBar.chooseDate"
                  )}
                </Typography>
              </Button>
            </Stack>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export type DateRangePickerProps = {
  onOpen?: () => void;
  initialOpen?: boolean;
  dateFormat?: string;
  pickerType?: PickerType;
  departureDateBuckets?: DeparturePriceBucket[];
  filters?: FilterItem[];
  initialMonth?: Date;
  forceModal?: boolean;
  deals?: Deal[];
} & TextFieldProps &
  Partial<CalendarProps>;

export const DateRangePicker = ({
  onOpen,
  initialOpen,
  anchorEl,
  startDate,
  endDate,
  focusedInput,
  onDatesChange = () => null,
  InputProps,
  InputLabelProps,
  dateFormat = DEFAULT_DATE_FORMAT,
  pickerType = PickerType.RANGE,
  departureDateBuckets = [],
  minBookingDays,
  minBookingDate,
  maxBookingDate,
  filters = [],
  initialMonth,
  forceModal,
  deals = [],
  forceDisplayBucketsAndDeals = false,
  ...rest
}: DateRangePickerProps) => {
  const [open, setOpen] = useState(false);
  const { t } = useI18nContext();
  const isTablet = !useMediaQuery(({ breakpoints }: Theme) =>
    breakpoints.up("desktop")
  );
  const value = useMemo(() => {
    if (startDate && endDate) {
      return `${dayjs(startDate).format(dateFormat)} - ${dayjs(endDate).format(
        dateFormat
      )}`;
    }
    if (startDate) {
      return dayjs(startDate).format(dateFormat);
    }
    return "";
  }, [startDate, endDate, dateFormat]);
  const [pickerAnchorEl, setPickerAnchorEl] = useState<HTMLElement | null>(
    null
  );
  const pickerRef = useCallback((node: HTMLElement | null) => {
    setPickerAnchorEl(node);
  }, []);

  const handleOpen = useCallback(() => {
    setOpen(true);
    onOpen?.();
  }, [onOpen]);

  const handleClose = () => {
    setOpen(false);
  };

  useEffect(() => {
    // Initialize picker open state based on initialOpen prop
    if (initialOpen && !isTablet) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      handleOpen();
    }
  }, [handleOpen, initialOpen, isTablet]);

  return (
    <>
      <SearchField
        inputRef={pickerRef}
        data-testid="dates-picker"
        label={t("searchBar.datesLabel")}
        placeholder={t("searchBar.datesPlaceholder")}
        {...rest}
        InputProps={{
          ...InputProps,
          readOnly: true,
        }}
        inputProps={{
          style: {
            cursor: "pointer",
          },
        }}
        InputLabelProps={{
          ...InputLabelProps,
          shrink: true,
        }}
        onClick={handleOpen}
        value={value}
      />
      <Calendar
        {...rest}
        open={open}
        onClose={handleClose}
        anchorEl={anchorEl ?? pickerAnchorEl}
        startDate={startDate ?? null}
        endDate={endDate ?? null}
        focusedInput={focusedInput ?? null}
        onDatesChange={onDatesChange}
        pickerType={pickerType}
        departureDateBuckets={departureDateBuckets}
        minBookingDays={minBookingDays}
        minBookingDate={minBookingDate}
        maxBookingDate={maxBookingDate}
        filters={filters}
        initialMonth={initialMonth}
        forceModal={forceModal}
        deals={deals}
        forceDisplayBucketsAndDeals={forceDisplayBucketsAndDeals}
      />
    </>
  );
};

export function isValidDateRange(startDate: Date, endDate: Date) {
  return !!(startDate && endDate && startDate <= endDate);
}
